/**
 * LoRa Message Adapter
 *
 * Converts between CrowdTalkie messages and LoRa message formats.
 * Handles encryption, signing, and format conversion for both backends.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import type {
  LoRaMessage,
  LoRaOutgoingMessage,
  LoRaChannelMapping,
  LoRaBackend,
  LoRaMessageType,
  LoRaVehicleSighting,
  LoRaPlateResponse,
  LoRaHandoffRequest,
} from './types'
import type { VehicleSighting, PlateRecord, VehicleType, VehicleDirection } from '../vehicles/types'
import type { HandoffRequest } from '../zones/types'

// ============= Message ID Generation =============

/**
 * Generate a unique message ID for LoRa messages
 */
export function generateLoRaMessageId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 8)
  return `lr-${timestamp}-${random}`
}

// ============= Channel Key Derivation =============

/**
 * Derive a LoRa channel key from room secret
 * Uses HKDF to create a 32-byte key compatible with both backends
 */
export async function deriveLoRaChannelKey(
  roomSecret: string,
  roomId: string
): Promise<Uint8Array> {
  const encoder = new TextEncoder()
  const keyMaterial = encoder.encode(roomSecret)
  const salt = encoder.encode(`crowdtalkie-lora-v1-${roomId}`)
  const info = encoder.encode('lora-channel-key')

  const baseKey = await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    'HKDF',
    false,
    ['deriveBits']
  )

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info,
    },
    baseKey,
    256
  )

  return new Uint8Array(derivedBits)
}

/**
 * Create a channel mapping for a room
 */
export async function createLoRaChannelMapping(
  roomId: string,
  roomSecret: string,
  backend: LoRaBackend,
  roomName?: string,
  meshtasticChannelIndex?: number
): Promise<LoRaChannelMapping> {
  const channelKey = await deriveLoRaChannelKey(roomSecret, roomId)
  const channelName = generateChannelName(roomId, roomName)

  return {
    roomId,
    roomSecret,
    channelKey,
    channelName,
    backend,
    meshtasticChannelIndex,
    createdAt: new Date(),
  }
}

/**
 * Generate a short channel name from room info
 * Max 11 characters for Meshtastic compatibility
 */
export function generateChannelName(roomId: string, roomName?: string): string {
  if (roomName) {
    return roomName
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 11)
      .toUpperCase() || `CT${roomId.slice(0, 9)}`
  }
  return `CT${roomId.slice(0, 9).toUpperCase()}`
}

// ============= Message Conversion =============

/**
 * Convert a CrowdTalkie outgoing message to LoRa format
 */
export function toLoRaMessage(
  outgoing: LoRaOutgoingMessage,
  backend: LoRaBackend
): LoRaMessage {
  return {
    id: generateLoRaMessageId(),
    type: outgoing.type,
    roomId: outgoing.roomId,
    payload: outgoing.payload,
    signature: outgoing.signature,
    senderId: outgoing.senderId,
    timestamp: Date.now(),
    backend,
  }
}

/**
 * Serialize a LoRa message for transmission
 * Creates a compact JSON representation
 */
export function serializeLoRaMessage(message: LoRaMessage): string {
  return JSON.stringify({
    i: message.id,
    t: message.type,
    r: message.roomId,
    p: message.payload,
    s: message.signature,
    f: message.senderId,
    ts: message.timestamp,
  })
}

/**
 * Deserialize a LoRa message from received data
 */
export function deserializeLoRaMessage(
  data: string,
  backend: LoRaBackend,
  rssi?: number,
  snr?: number,
  hopCount?: number
): LoRaMessage | null {
  try {
    const parsed = JSON.parse(data)
    return {
      id: parsed.i || generateLoRaMessageId(),
      type: parsed.t || 'text',
      roomId: parsed.r || '',
      payload: parsed.p || '',
      signature: parsed.s,
      senderId: parsed.f,
      timestamp: parsed.ts || Date.now(),
      backend,
      rssi,
      snr,
      hopCount,
    }
  } catch (error) {
    console.error('[LoRa] Failed to deserialize message:', error)
    return null
  }
}

// ============= Message Encoding for Backends =============

/**
 * Type code mapping for LoRa messages
 */
const TYPE_TO_CODE: Record<LoRaMessageType, number> = {
  text: 0x01,
  state_sync: 0x02,
  poi: 0x03,
  ping: 0x04,
  pong: 0x05,
  vehicle: 0x10,
  plate_lookup: 0x11,
  plate_response: 0x12,
  handoff_req: 0x13,
  handoff_ack: 0x14,
}

const CODE_TO_TYPE: Record<number, LoRaMessageType> = {
  0x01: 'text',
  0x02: 'state_sync',
  0x03: 'poi',
  0x04: 'ping',
  0x05: 'pong',
  0x10: 'vehicle',
  0x11: 'plate_lookup',
  0x12: 'plate_response',
  0x13: 'handoff_req',
  0x14: 'handoff_ack',
}

/**
 * Encode a message for Meshtastic PRIVATE_APP
 */
export function encodeForMeshtastic(message: LoRaMessage): Uint8Array {
  const APP_ID = 'CT01'
  const TYPE_MAP = TYPE_TO_CODE

  const appIdBytes = new TextEncoder().encode(APP_ID)
  const typeByte = new Uint8Array([TYPE_MAP[message.type] || 0x01])
  const jsonBytes = new TextEncoder().encode(JSON.stringify({
    r: message.roomId,
    p: message.payload,
    s: message.signature,
    i: message.senderId,
    t: message.timestamp,
  }))

  const result = new Uint8Array(appIdBytes.length + 1 + jsonBytes.length)
  result.set(appIdBytes, 0)
  result.set(typeByte, 4)
  result.set(jsonBytes, 5)

  return result
}

/**
 * Decode a Meshtastic PRIVATE_APP message (CT01 JSON format only)
 * For CT02 binary format, use decodeBinaryMessage from binary-encoding.ts
 */
export function decodeFromMeshtastic(
  payload: Uint8Array,
  rssi?: number,
  snr?: number
): LoRaMessage | null {
  const APP_ID = 'CT01'
  const TYPE_MAP = CODE_TO_TYPE

  try {
    const appId = new TextDecoder().decode(payload.slice(0, 4))
    if (appId !== APP_ID) return null

    const typeByte = payload[4] || 0x01
    const jsonBytes = payload.slice(5)
    const data = JSON.parse(new TextDecoder().decode(jsonBytes))

    return {
      id: generateLoRaMessageId(),
      type: TYPE_MAP[typeByte] || 'text',
      roomId: data.r || '',
      payload: data.p || '',
      signature: data.s,
      senderId: data.i,
      timestamp: data.t || Date.now(),
      backend: 'meshtastic',
      rssi,
      snr,
    }
  } catch (error) {
    console.error('[LoRa] Failed to decode Meshtastic message:', error)
    return null
  }
}

/**
 * Check message format version from payload
 * Returns 'CT01' for JSON format, 'CT02' for binary format, or null if unknown
 */
export function detectMessageFormat(payload: Uint8Array): 'CT01' | 'CT02' | null {
  if (payload.length < 4) return null

  const appId = new TextDecoder().decode(payload.slice(0, 4))

  if (appId === 'CT01') return 'CT01'
  if (appId === 'CT02') return 'CT02'

  return null
}

/**
 * Encode a message for MeshCore BINARY frame (legacy)
 * @deprecated Use encodeForMeshCoreGroup for new messages
 */
export function encodeForMeshCore(message: LoRaMessage): Uint8Array {
  // Same format as Meshtastic for interoperability
  return encodeForMeshtastic(message)
}

/**
 * Encode a message for MeshCore using group messaging
 * This hides the MeshCore node identity in packet routing
 *
 * Format: [GROUP_NAME (null-terminated)][CT01/CT02 payload]
 */
export function encodeForMeshCoreGroup(message: LoRaMessage): Uint8Array {
  const GROUP_NAME = '#crowdtalkie'
  const APP_ID = 'CT01'
  const TYPE_MAP = TYPE_TO_CODE

  // Build the CrowdTalkie payload
  const appIdBytes = new TextEncoder().encode(APP_ID)
  const typeByte = new Uint8Array([TYPE_MAP[message.type] || 0x01])
  const jsonBytes = new TextEncoder().encode(JSON.stringify({
    r: message.roomId,
    p: message.payload,
    s: message.signature,
    i: message.senderId,
    t: message.timestamp,
  }))

  const ctPayload = new Uint8Array(appIdBytes.length + 1 + jsonBytes.length)
  ctPayload.set(appIdBytes, 0)
  ctPayload.set(typeByte, 4)
  ctPayload.set(jsonBytes, 5)

  // Build the group message: [GROUP_NAME\0][CT_PAYLOAD]
  const groupNameBytes = new TextEncoder().encode(GROUP_NAME + '\0')
  const result = new Uint8Array(groupNameBytes.length + ctPayload.length)
  result.set(groupNameBytes, 0)
  result.set(ctPayload, groupNameBytes.length)

  return result
}

/**
 * Decode a MeshCore BINARY frame (legacy)
 */
export function decodeFromMeshCore(
  payload: Uint8Array,
  rssi?: number,
  snr?: number
): LoRaMessage | null {
  const result = decodeFromMeshtastic(payload, rssi, snr)
  if (result) {
    result.backend = 'meshcore'
  }
  return result
}

/**
 * Decode a MeshCore group message
 * Expects format: [GROUP_NAME\0][CT01/CT02 payload]
 */
export function decodeFromMeshCoreGroup(
  payload: Uint8Array,
  rssi?: number,
  snr?: number
): LoRaMessage | null {
  const GROUP_NAME = '#crowdtalkie'
  const APP_ID = 'CT01'
  const TYPE_MAP = CODE_TO_TYPE

  try {
    // Find null terminator for group name
    const nullIndex = payload.indexOf(0)
    if (nullIndex === -1) {
      console.warn('[LoRa] Invalid group message: no null terminator')
      return null
    }

    const groupName = new TextDecoder().decode(payload.slice(0, nullIndex))
    if (groupName !== GROUP_NAME) {
      return null // Not our group
    }

    const ctPayload = payload.slice(nullIndex + 1)

    // Parse CrowdTalkie payload (same as decodeFromMeshtastic)
    const appId = new TextDecoder().decode(ctPayload.slice(0, 4))
    if (appId !== APP_ID) return null

    const typeByte = ctPayload[4] || 0x01
    const jsonBytes = ctPayload.slice(5)
    const data = JSON.parse(new TextDecoder().decode(jsonBytes))

    return {
      id: generateLoRaMessageId(),
      type: TYPE_MAP[typeByte] || 'text',
      roomId: data.r || '',
      payload: data.p || '',
      signature: data.s,
      senderId: data.i,
      timestamp: data.t || Date.now(),
      backend: 'meshcore',
      rssi,
      snr,
    }
  } catch (error) {
    console.error('[LoRa] Failed to decode MeshCore group message:', error)
    return null
  }
}

// ============= Payload Compression =============

/**
 * Compress a message payload for LoRa transmission
 * Uses simple dictionary encoding for common strings
 */
export function compressPayload(payload: string): string {
  // For now, just return the payload as-is
  // TODO: Implement LZ4 or similar compression for state_sync messages
  return payload
}

/**
 * Decompress a message payload
 */
export function decompressPayload(compressed: string): string {
  // For now, just return as-is
  return compressed
}

// ============= Message Validation =============

// ============= Vehicle Sighting Encoding =============

/**
 * Vehicle type to code mapping
 */
const VEHICLE_TYPE_TO_CODE: Record<string, number> = {
  sedan: 0, suv: 1, van: 2, truck: 3, unmarked: 4, other: 5,
}

const CODE_TO_VEHICLE_TYPE: Partial<Record<number, VehicleType>> = {
  0: 'sedan', 1: 'suv', 2: 'van', 3: 'truck', 4: 'unmarked', 5: 'other',
}

/**
 * Direction to code mapping
 */
const DIRECTION_TO_CODE: Record<string, number> = {
  N: 0, NE: 1, E: 2, SE: 3, S: 4, SW: 5, W: 6, NW: 7, stationary: 8,
}

const CODE_TO_DIRECTION: Partial<Record<number, VehicleDirection>> = {
  0: 'N', 1: 'NE', 2: 'E', 3: 'SE', 4: 'S', 5: 'SW', 6: 'W', 7: 'NW', 8: 'stationary',
}

/**
 * Color to code mapping
 */
const COLOR_TO_CODE: Record<string, number> = {
  black: 0, white: 1, silver: 2, gray: 3, blue: 4, red: 5, green: 6, brown: 7, other: 8,
}

const CODE_TO_COLOR: Record<number, string> = {
  0: 'black', 1: 'white', 2: 'silver', 3: 'gray', 4: 'blue', 5: 'red', 6: 'green', 7: 'brown', 8: 'other',
}

/**
 * Convert a VehicleSighting to compact LoRa format
 */
export function vehicleSightingToLoRa(sighting: VehicleSighting): LoRaVehicleSighting {
  return {
    p: sighting.plate,
    v: sighting.vehicleType ? VEHICLE_TYPE_TO_CODE[sighting.vehicleType] : undefined,
    c: sighting.vehicleColor ? COLOR_TO_CODE[sighting.vehicleColor] ?? 8 : undefined,
    d: sighting.direction ? DIRECTION_TO_CODE[sighting.direction] : undefined,
    f: sighting.isFollowing || undefined,
    la: Math.round(sighting.location.latitude * 1000000),
    lo: Math.round(sighting.location.longitude * 1000000),
    de: sighting.description?.slice(0, 50),
  }
}

/**
 * Convert a compact LoRa vehicle sighting back to full format
 */
export function loRaToVehicleSighting(
  data: LoRaVehicleSighting,
  senderId: string,
  senderName: string,
  _roomId: string
): Partial<VehicleSighting> {
  return {
    plate: data.p,
    vehicleType: data.v !== undefined ? CODE_TO_VEHICLE_TYPE[data.v] : undefined,
    vehicleColor: data.c !== undefined ? CODE_TO_COLOR[data.c] : undefined,
    direction: data.d !== undefined ? CODE_TO_DIRECTION[data.d] : undefined,
    isFollowing: data.f ?? false,
    location: {
      latitude: data.la / 1000000,
      longitude: data.lo / 1000000,
    },
    description: data.de,
    senderId,
    senderName,
  }
}

/**
 * Convert a HandoffRequest to compact LoRa format
 */
export function handoffRequestToLoRa(request: HandoffRequest): LoRaHandoffRequest {
  return {
    r: request.id,
    v: vehicleSightingToLoRa(request.vehicleSighting),
    z: request.fromZoneName,
    st: request.street,
  }
}

/**
 * Convert a PlateRecord to LoRa response format
 */
export function plateRecordToLoRaResponse(
  requestId: string,
  record: PlateRecord
): LoRaPlateResponse {
  const statusMap: Record<string, number> = {
    unknown: 0,
    confirmed_hostile: 1,
    suspected: 2,
    cleared: 3,
  }

  return {
    r: requestId,
    p: record.plate,
    s: statusMap[record.status] ?? 0,
    c: record.sightingCount,
  }
}

/**
 * Create a LoRa message for a vehicle sighting
 */
export function createVehicleSightingMessage(
  sighting: VehicleSighting,
  roomId: string,
  backend: LoRaBackend
): LoRaMessage {
  const loRaSighting = vehicleSightingToLoRa(sighting)

  return {
    id: generateLoRaMessageId(),
    type: 'vehicle',
    roomId,
    payload: JSON.stringify(loRaSighting),
    senderId: sighting.senderId,
    timestamp: Date.now(),
    backend,
  }
}

/**
 * Create a LoRa message for a handoff request
 */
export function createHandoffRequestMessage(
  request: HandoffRequest,
  roomId: string,
  backend: LoRaBackend
): LoRaMessage {
  const loRaRequest = handoffRequestToLoRa(request)

  return {
    id: generateLoRaMessageId(),
    type: 'handoff_req',
    roomId,
    payload: JSON.stringify(loRaRequest),
    senderId: request.fromPatrol,
    timestamp: Date.now(),
    backend,
  }
}

// ============= Message Validation =============

/**
 * Validate a LoRa message
 */
export function validateLoRaMessage(message: LoRaMessage): boolean {
  if (!message.id || !message.roomId) {
    return false
  }

  const validTypes = Object.keys(TYPE_TO_CODE)
  if (!validTypes.includes(message.type)) {
    return false
  }

  // Check timestamp is reasonable (within 24 hours)
  const now = Date.now()
  const timeDiff = Math.abs(now - message.timestamp)
  if (timeDiff > 24 * 60 * 60 * 1000) {
    console.warn('[LoRa] Message timestamp out of range:', message.timestamp)
    return false
  }

  return true
}

/**
 * Check if a message should be processed (not a duplicate)
 */
export function isNewMessage(
  message: LoRaMessage,
  processedIds: Set<string>
): boolean {
  if (processedIds.has(message.id)) {
    return false
  }

  // Also check for duplicate payload/timestamp combinations
  const key = `${message.roomId}:${message.timestamp}:${message.payload.slice(0, 32)}`
  if (processedIds.has(key)) {
    return false
  }

  return true
}

/**
 * Mark a message as processed
 */
export function markMessageProcessed(
  message: LoRaMessage,
  processedIds: Set<string>
): void {
  processedIds.add(message.id)

  // Also add the payload key
  const key = `${message.roomId}:${message.timestamp}:${message.payload.slice(0, 32)}`
  processedIds.add(key)

  // Cleanup old IDs (keep last 1000)
  if (processedIds.size > 1000) {
    const idsArray = Array.from(processedIds)
    const toRemove = idsArray.slice(0, idsArray.length - 1000)
    for (const id of toRemove) {
      processedIds.delete(id)
    }
  }
}

// ============= Utilities =============

/**
 * Estimate message size in bytes
 */
export function estimateMessageSize(message: LoRaMessage): number {
  const serialized = serializeLoRaMessage(message)
  return new TextEncoder().encode(serialized).length + 5 // +5 for CT01 header and type
}

/**
 * Check if a message is too large for LoRa
 */
export function isMessageTooLarge(message: LoRaMessage, maxSize: number = 230): boolean {
  return estimateMessageSize(message) > maxSize
}

/**
 * Fragment a large message into smaller parts
 * Returns an array of messages that can be reassembled
 */
export function fragmentMessage(
  message: LoRaMessage,
  maxPayloadSize: number = 200
): LoRaMessage[] {
  const payload = message.payload
  if (payload.length <= maxPayloadSize) {
    return [message]
  }

  const fragments: LoRaMessage[] = []
  const totalParts = Math.ceil(payload.length / maxPayloadSize)

  for (let i = 0; i < totalParts; i++) {
    const start = i * maxPayloadSize
    const end = Math.min(start + maxPayloadSize, payload.length)
    const fragmentPayload = payload.slice(start, end)

    fragments.push({
      ...message,
      id: `${message.id}-${i + 1}of${totalParts}`,
      payload: fragmentPayload,
    })
  }

  return fragments
}

/**
 * Check if a message is a fragment
 */
export function isFragment(message: LoRaMessage): boolean {
  return /^\w+-\d+of\d+$/.test(message.id)
}

/**
 * Get fragment info from message ID
 */
export function getFragmentInfo(message: LoRaMessage): { part: number; total: number; baseId: string } | null {
  const match = message.id.match(/^(.+)-(\d+)of(\d+)$/)
  if (!match) return null

  return {
    baseId: match[1]!,
    part: parseInt(match[2]!, 10),
    total: parseInt(match[3]!, 10),
  }
}
